import { TIMEOUT_ERROR_CODE } from 'config';
import { useSnackbar } from 'notistack';
import { useContext } from 'react';
import { i18n } from 'i18n';
import {
  MutationFunction,
  QueryFunction,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from 'react-query';
import useScenario from 'modules/project/providers/scenario/use';
import { Pager } from './api';
import ApiContext, { IApiContext } from './context';

export interface PageParams {
  page: number,
  pageSize: number
}

export interface PagedListOfT<T> {
  items: T[];
  pager: Pager;
}

const retry = (failureCount: number, error: any) => (
  error
  && error.status
  && error.status === TIMEOUT_ERROR_CODE
  && failureCount < 2
);

const useApi = (): IApiContext => {
  const context = useContext(ApiContext);
  if (context === null) {
    throw new Error(i18n._('No api context available'));
  }
  return context;
};

type QueryKeyFor<P> =
  | readonly [key: string]
  | readonly [key: string, params: NonNullable<P>];

export const makeListQuery = <P, Data, TError = unknown>(
  key: string,
  queryFnFactory: (api: IApiContext, params: P) => QueryFunction<Data, QueryKeyFor<P>>,
) => (
  params: P,
  options?: Omit<
    UseQueryOptions<Data, TError, Data, QueryKeyFor<P>>,
    'queryKey' | 'queryFn'
  >,
) => {
    const api = useApi();

    if (params != null) {
      const queryKey = [key, params as NonNullable<P>] as const;

      return useQuery<Data, TError, Data, typeof queryKey>({
        queryKey,
        queryFn: queryFnFactory(api, params),
        retry,
        keepPreviousData: true,
        ...(options as Omit<
          UseQueryOptions<Data, TError, Data, typeof queryKey>,
          'queryKey' | 'queryFn'
        >),
      });
    }

    // branch: [key]
    const queryKey = [key] as const;

    return useQuery<Data, TError, Data, typeof queryKey>({
      queryKey,
      queryFn: queryFnFactory(api, params),
      retry,
      keepPreviousData: true,
      ...(options as Omit<
        UseQueryOptions<Data, TError, Data, typeof queryKey>,
        'queryKey' | 'queryFn'
      >),
    });
  };

export const makeInfiniteListQuery = <P, Data, TError = unknown>(
  key: string,
  queryFnFactory: (api: IApiContext, params?: P) => QueryFunction<PagedListOfT<Data>, string>,
) => (
  params?: P,
  options?: Omit<
    UseInfiniteQueryOptions<
      PagedListOfT<Data>,
      TError,
      PagedListOfT<Data>,
      PagedListOfT<Data>,
      string
    >,
    'queryKey' | 'queryFn'
  >,
) => {
    const api = useApi();
    const queryKey = params ? `${key}:${JSON.stringify(params)}` : key;

    return useInfiniteQuery<PagedListOfT<Data>, TError, PagedListOfT<Data>, string>(
      queryKey,
      queryFnFactory(api, params),
      {
        getPreviousPageParam: (firstPage) => {
          if (firstPage.pager.page !== 1) {
            return {
              page: firstPage.pager.page - 1,
              pageSize: firstPage.pager.pageSize,
            };
          }
          return false;
        },
        getNextPageParam: (lastPage) => {
          if (lastPage.pager.page < lastPage.pager.pageCount) {
            return {
              page: lastPage.pager.page + 1,
              pageSize: lastPage.pager.pageSize,
            };
          }
          return false;
        },
        retry,
        ...options,
      },
    );
  };

export const makeGetQuery = <TGet, TParams = unknown, TError = unknown>(
  key: string,
  queryFnFactory: (api: IApiContext, params: TParams) => QueryFunction<TGet, QueryKeyFor<TParams>>,
) => (
  params: TParams,
  options?: Omit<
    UseQueryOptions<TGet, TError, TGet, QueryKeyFor<TParams>>,
    'queryKey' | 'queryFn'
  >,
) => {
    const api = useApi();

    if (params != null) {
      // [key, params]
      const queryKey = [key, params as NonNullable<TParams>] as const;

      return useQuery<TGet, TError, TGet, typeof queryKey>({
        queryKey,
        queryFn: queryFnFactory(api, params) as QueryFunction<TGet, typeof queryKey>,
        retry,
        ...(options as Omit<
          UseQueryOptions<TGet, TError, TGet, typeof queryKey>,
          'queryKey' | 'queryFn'
        >),
      });
    }

    // [key]
    const queryKey = [key] as const;

    return useQuery<TGet, TError, TGet, typeof queryKey>({
      queryKey,
      queryFn: queryFnFactory(api, params) as QueryFunction<TGet, typeof queryKey>,
      retry,
      ...(options as Omit<
        UseQueryOptions<TGet, TError, TGet, typeof queryKey>,
        'queryKey' | 'queryFn'
      >),
    });
  };

export const makeCreateMutation = <TData, TModel>(
  listKey: string | null,
  mutationFnFactory: (api: IApiContext, scenarioId: string | null) => MutationFunction<TData, TModel>,
  successMessageFn: (model: TModel) => string,
  invalidateKeys: string[] = []) => (options?: UseMutationOptions<TData, unknown, TModel>) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { activeScenarioId } = useScenario();
    return useMutation<TData, unknown, TModel>(
      mutationFnFactory(api, activeScenarioId),
      {
        ...options,
        onSuccess: (...args) => {
          if (options?.onSuccess) {
            options.onSuccess(...args);
          }
          enqueueSnackbar(successMessageFn(args[1]), {
            variant: 'success',
          });

          listKey && queryClient.invalidateQueries(listKey, { exact: false });
          invalidateKeys.forEach((key) => queryClient.invalidateQueries(key, { exact: false }));
        },
        retry,
      },
    );
  };

export const makeUpdateMutation = <TData, TModel>(
  getKey: string,
  listKey: string | null,
  mutationFnFactory: (api: IApiContext, entityId: string, scenarioId: string | null) => MutationFunction<TData, TModel>,
  successMessageFn: (model: TModel) => string,
  invalidateKeys: string[] = [],
  useDefaultOnSuccess = true,
) => (entityId: string, options?: UseMutationOptions<TData, unknown, TModel>) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { activeScenarioId } = useScenario();
    return useMutation<TData, unknown, TModel>(
      mutationFnFactory(api, entityId, activeScenarioId),
      {
        ...options,
        onSuccess: (...args) => {
          if (options?.onSuccess) {
            options.onSuccess(...args);
          }

          if (!useDefaultOnSuccess) {
            return;
          }

          const message = successMessageFn(args[1]);
          if (message) {
            enqueueSnackbar(message, { variant: 'success' });
          }

          listKey && queryClient.invalidateQueries(listKey, { exact: false });
          queryClient.invalidateQueries([getKey, entityId]);
          invalidateKeys.forEach((key) => queryClient.invalidateQueries(key, { exact: false }));
      },
      retry,
    },
  );
};

export const makeDeleteMutation = <TData, TModel>(
  getKey: string,
  listKey: string | null,
  mutationFnFactory: (api: IApiContext, scenarioId: string | null) => MutationFunction<TData, TModel>,
  successMessageFn: (model: TModel) => string,
  invalidationId: (model: TModel) => string,
  invalidateKeys: string[] = [],
  errorMessageFn?: string,
) => (options?: UseMutationOptions<TData, unknown, TModel>) => {
  const api = useApi();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();
  const { activeScenarioId } = useScenario();

  return useMutation<TData, unknown, TModel>(
    mutationFnFactory(api, activeScenarioId),
    {
      ...options,
      onSuccess: (...args) => {
        if (options?.onSuccess) {
          options.onSuccess(...args);
        }

        enqueueSnackbar(successMessageFn(args[1]), {
          variant: 'success',
        });

        listKey && queryClient.invalidateQueries(listKey, { exact: false });
        queryClient.invalidateQueries([getKey, invalidationId(args[1])]);
        invalidateKeys.forEach((key) => queryClient.invalidateQueries(key, { exact: false }));
      },
      onError: (...error) => {
        if (options?.onError) {
          options.onError(...error);
        }

        if (errorMessageFn) {
          enqueueSnackbar(errorMessageFn, {
            variant: 'error',
          });
        }
      },
      retry,
    },
  );
};

export const makeRemoveQueries = (queryKey: QueryKey | undefined) => {
  const queryClient = useQueryClient();

  const execute = () => queryClient.removeQueries({ queryKey });

  return {
    execute,
  };
};

export default useApi;
